import React from 'react';
import {createStyles, makeStyles, Theme as MuiTheme, useTheme} from '@material-ui/core/styles';
import {fade} from '@material-ui/core/styles/colorManipulator';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import Avatar from '@material-ui/core/Avatar';
import Box from '@material-ui/core/Box';
import Chip from '@material-ui/core/Chip';
import Dialog from '@material-ui/core/Dialog';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import IconButton from '@material-ui/core/IconButton';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemAvatar from '@material-ui/core/ListItemAvatar';
import ListItemText from '@material-ui/core/ListItemText';
import Typography from '@material-ui/core/Typography';
import CloseIcon from '@material-ui/icons/Close';
import CreateIcon from '@material-ui/icons/Create';
import {observer} from 'mobx-react-lite';
import {useRouter} from 'next/router';
import {themes, StoryTheme} from '../util/story_themes';
import {characterStore} from '../utils/characters';

const useStyles = makeStyles((theme: MuiTheme) => {
    const mediumWeight = Number(theme.typography.fontWeightMedium ?? 500);

    return createStyles({
        dialogPaper: {
            borderRadius: 20,
            width: 'min(540px, calc(100vw - 32px))',
        },
        dialogPaperFullScreen: {
            borderRadius: 0,
            width: '100vw',
            height: '100vh',
        },
        titleRoot: {
            paddingBottom: theme.spacing(1),
            paddingRight: theme.spacing(1),
        },
        titleContent: {
            display: 'flex',
            alignItems: 'flex-start',
        },
        titleCopy: {
            flex: 1,
            minWidth: 0,
            paddingRight: theme.spacing(1.5),
        },
        titleHeading: {
            fontFamily: theme.typography.h1.fontFamily,
            fontWeight: 600,
            fontSize: '1.5rem',
            lineHeight: 1.3,
        },
        titleSubheading: {
            color: theme.palette.text.secondary,
            marginTop: theme.spacing(0.5),
        },
        closeButton: {
            marginTop: theme.spacing(-0.5),
            marginRight: theme.spacing(-1),
            color: theme.palette.text.secondary,
        },
        content: {
            padding: theme.spacing(1.5, 2, 2.25),
            [theme.breakpoints.down('xs')]: {
                padding: theme.spacing(1.25, 1.5, 1.75),
            },
        },
        list: {
            padding: 0,
        },
        listItem: {
            marginTop: theme.spacing(0.5),
            marginBottom: theme.spacing(0.5),
            borderRadius: theme.spacing(1.25),
            padding: theme.spacing(1.25, 1.75),
            transition: theme.transitions.create(['background-color', 'box-shadow', 'transform'], {
                duration: theme.transitions.duration.short,
            }),
            '&:hover': {
                backgroundColor: theme.palette.action.hover,
            },
            '&:focus-visible': {
                outline: `2px solid ${fade(theme.palette.primary.main, 0.5)}`,
                outlineOffset: 2,
            },
        },
        listItemAvatar: {
            minWidth: theme.spacing(6.5),
        },
        avatar: {
            borderRadius: 12,
            backgroundColor: fade(theme.palette.primary.main, 0.12),
            color: theme.palette.primary.main,
            width: theme.spacing(5),
            height: theme.spacing(5),
        },
        icon: {
            fontSize: '1.6rem',
        },
        primaryRow: {
            display: 'flex',
            alignItems: 'center',
            flexWrap: 'wrap',
        },
        primaryText: {
            fontWeight: mediumWeight,
            marginRight: theme.spacing(1),
        },
        secondaryText: {
            color: theme.palette.text.secondary,
            marginTop: theme.spacing(0.5),
        },
        adultChip: {
            backgroundColor: fade(theme.palette.primary.main, 0.14),
            color: theme.palette.primary.light,
            fontWeight: mediumWeight,
            fontSize: '0.72rem',
            height: 22,
            pointerEvents: 'none',
        },
    });
});

export interface SimpleDialogProps {
    open: boolean;
    onClose: (value: string | null) => void;
}

// Starting from saved characters goes to its own page, so every place that opens this chooser offers it the same way
const STORY_WITH_CHARACTERS: StoryTheme = {
    name: 'characters',
    displayName: 'Write your own erotica',
    icon: CreateIcon,
    description: "Describe your fantasy and we'll write it.",
    adultOnly: true,
};

const SimpleDialog = observer((props: SimpleDialogProps) => {
    const classes = useStyles();
    const theme = useTheme();
    const router = useRouter();
    const fullScreen = useMediaQuery(theme.breakpoints.down('xs'));
    const openedAtRef = React.useRef<number>(0);
    const {onClose, open} = props;

    React.useEffect(() => {
        if (open) {
            openedAtRef.current = Date.now();
        }
    }, [open]);

    const handleDialogClose = (_event: unknown, reason?: 'backdropClick' | 'escapeKeyDown') => {
        if (reason === 'backdropClick') {
            const elapsed = Date.now() - openedAtRef.current;
            if (elapsed < 250) {
                return;
            }
        }
        onClose(null);
    };

    const handleListItemClick = (value: string) => {
        if (value === STORY_WITH_CHARACTERS.name) {
            onClose(null);
            router.push('/characters/start');
            return;
        }
        onClose(value);
    };

    // erotica is what nearly everyone reads (82% of parts written in October), so it leads; writing your own comes
    // next; the other genres follow
    const erotica = themes.filter((t: StoryTheme) => t.name === 'erotica');
    // the other genres, by how much they're read: romance, sci-fi, then Game of Thrones
    const genreOrder = ['romance', 'scifi', 'got'];
    const otherGenres = themes.filter((t: StoryTheme) => t.name !== 'erotica')
        .sort((a: StoryTheme, b: StoryTheme) => genreOrder.indexOf(a.name) - genreOrder.indexOf(b.name));
    const choices = [...erotica, ...(characterStore.enabled ? [STORY_WITH_CHARACTERS] : []), ...otherGenres];

    const paperClassName = fullScreen
        ? `${classes.dialogPaper} ${classes.dialogPaperFullScreen}`
        : classes.dialogPaper;

    return (
        <Dialog
            onClose={handleDialogClose}
            aria-labelledby="choose-story-theme"
            open={open}
            fullWidth
            maxWidth="sm"
            scroll="paper"
            fullScreen={fullScreen}
            classes={{paper: paperClassName}}
        >
            <DialogTitle id="choose-story-theme" disableTypography className={classes.titleRoot}>
                <Box className={classes.titleContent}>
                    <Box className={classes.titleCopy}>
                        <Typography variant="h6" className={classes.titleHeading}>
                            Start a story
                        </Typography>
                        <Typography variant="body2" className={classes.titleSubheading}>
                            Jump into one of our openings, or write your own.
                        </Typography>
                    </Box>
                    <IconButton
                        aria-label="Close theme selector"
                        onClick={() => onClose(null)}
                        className={classes.closeButton}
                    >
                        <CloseIcon fontSize="small"/>
                    </IconButton>
                </Box>
            </DialogTitle>
            <DialogContent dividers className={classes.content}>
                <List disablePadding className={classes.list}>
                    {choices.map((themeItem: StoryTheme) => {
                        const Icon = themeItem.icon;
                        return (
                            <ListItem
                                button
                                onClick={() => handleListItemClick(themeItem.name)}
                                key={themeItem.name}
                                className={classes.listItem}
                                alignItems="flex-start"
                            >
                                <ListItemAvatar classes={{root: classes.listItemAvatar}}>
                                    <Avatar variant="rounded" className={classes.avatar}>
                                        <Icon className={classes.icon}/>
                                    </Avatar>
                                </ListItemAvatar>
                                <ListItemText
                                    disableTypography
                                    primary={
                                        <Box className={classes.primaryRow}>
                                            <Typography variant="subtitle1" className={classes.primaryText}>
                                                {themeItem.displayName}
                                            </Typography>
                                        </Box>
                                    }
                                    secondary={
                                        <Typography variant="body2" className={classes.secondaryText}>
                                            {themeItem.description}
                                        </Typography>
                                    }
                                />
                            </ListItem>
                        );
                    })}
                </List>
            </DialogContent>
        </Dialog>
    );
});

export interface ChooseThemeDialogProps {
    open: boolean;
    onClose: (value: string | null) => void;
}

export default function ChooseThemeDialog(props: ChooseThemeDialogProps) {
    const {open, onClose} = props;

    return (
        <SimpleDialog
            open={open}
            onClose={onClose}
        />
    );
}
